import { ProjectField } from "./database/types/projects";
import { useCallback, useEffect, useState } from "react";
import { Field } from "../components/common/form/FieldHandler";
import _ from "lodash";

type FormField =
  | (Omit<ProjectField, "key" | "options"> & {
      key?: string;
      options?: null | string | string[] | ((values: Record<string, Field["value"]>) => string[]);
    })
  | (Omit<Field, "key" | "options" | "value"> & {
      key?: string;
      options?: null | string | string[] | ((values: Record<string, Field["value"]>) => string[]);
      value?: Field["value"];
    });

export function useForm(
  fields: Record<string, FormField>,
  validationFunc?: (fields: Record<string, Field>) => Record<string, { message: string }>,
  validationTrigger: "instant" | "blur" = "blur"
) {
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, Partial<Omit<Field, "type" | "value">>>>({});
  const [values, setValues] = useState<Record<string, Field["value"]>>({});
  const [cleanValues, setCleanValues] = useState<Record<string, Field["value"]>>({});
  const [errors, setErrors] = useState<Record<string, { message: string }>>({});

  const completeFields = Object.fromEntries<Field>(
    Object.entries(fields || {}).map(([key, field]) => [
      key,
      {
        key, // may be overridden by the key in `value`, if present
        ...field,
        options: typeof field.options === "function" ? field.options(values) : field.options,
        ...fieldOverrides[key],
        value: values[key],
        error: key in errors ? errors[key] : undefined,
      } as Field,
    ])
  );

  const validate = useCallback(() => {
    if (validationFunc) {
      setErrors(validationFunc(completeFields));
    }
  }, [validationFunc, completeFields]);

  const reset = useCallback(() => {
    setValues((v) => (_.isEqual(v, cleanValues) ? v : cleanValues));
  }, [cleanValues]);
  const setValues_ = useCallback((values: Record<string, Field["value"]>, resetClean: boolean = false) => {
    setValues((v) => (_.isEqual(v, values) ? v : values));
    if (resetClean) {
      setCleanValues((v) => (_.isEqual(v, values) ? v : values));
    }
  }, []);
  const setValue = useCallback(
    (key: string, value: Field["value"], resetClean: boolean = false) => {
      setValues((v) => (_.isEqual(v[key], value) ? v : { ...v, [key]: value }));
      if (resetClean) {
        setCleanValues((v) => (_.isEqual(v[key], value) ? v : { ...v, [key]: value }));
      }
      if (validationTrigger === "instant") {
        validate();
      }
    },
    [validate, validationTrigger]
  );

  const handleChange = useCallback(
    (key: string, value: Field["value"]) => {
      setValue(key, value);
    },
    [setValue]
  );
  const handleBlur = useCallback(() => {
    if (validationTrigger === "blur") {
      validate();
    }
  }, [validate, validationTrigger]);

  const setFieldOverride = useCallback((key: string, override: Partial<Omit<ProjectField, "type">> | null) => {
    if (override) {
      setFieldOverrides((o) => ({ ...o, [key]: override }));
    }
  }, []);

  // apply values from external fields (usually for previous values), unless we have locally changed the value already
  useEffect(() => {
    for (const [key, field] of Object.entries(fields)) {
      if ("value" in field) {
        setCleanValues((v) => (_.isEqual(v[key], field.value) ? v : { ...v, [key]: field.value }));
        if (_.isEqual(cleanValues[key], values[key])) {
          setValue(key, field.value, true);
        }
      }
    }
  }, [cleanValues, fields, setValue, values]);

  return {
    setFieldOverride,
    fields: completeFields,
    setValues: setValues_,
    setValue,
    reset,
    values,
    handleChange,
    handleBlur,
    dirty: !_.isEqual(values, cleanValues),
  };
}
